import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportStatusTracker = () => {
  const [selectedReport, setSelectedReport] = useState(null);

  const reportStatuses = [
    {
      id: 'rpt-001',
      title: 'EU ETS Surrender Report 2024',
      type: 'EU ETS',
      status: 'submitted',
      submissionDate: '2024-12-15',
      deadline: '2025-04-30',
      authority: 'European Commission',
      acknowledgment: 'EC-2024-ETS-001247',
      progress: [
        { stage: 'Generated', completed: true, date: '2024-12-10', user: 'Sarah Mitchell' },
        { stage: 'Reviewed', completed: true, date: '2024-12-12', user: 'Michael Chen' },
        { stage: 'Approved', completed: true, date: '2024-12-14', user: 'Emma Thompson' },
        { stage: 'Submitted', completed: true, date: '2024-12-15', user: 'System' },
        { stage: 'Acknowledged', completed: false, date: null, user: null }
      ],
      documents: [
        { name: 'Main Report.pdf', size: '2.4 MB', type: 'pdf' },
        { name: 'Emissions Data.xlsx', size: '890 KB', type: 'excel' },
        { name: 'Verification Statement.pdf', size: '1.2 MB', type: 'pdf' }
      ]
    },
    {
      id: 'rpt-002',
      title: 'FuelEU Maritime Statement 2024',
      type: 'FuelEU',
      status: 'in-review',
      submissionDate: null,
      deadline: '2025-03-31',
      authority: 'Flag State Authority',
      acknowledgment: null,
      progress: [
        { stage: 'Generated', completed: true, date: '2024-12-18', user: 'David Rodriguez' },
        { stage: 'Reviewed', completed: true, date: '2024-12-19', user: 'Sarah Mitchell' },
        { stage: 'Approved', completed: false, date: null, user: null },
        { stage: 'Submitted', completed: false, date: null, user: null },
        { stage: 'Acknowledged', completed: false, date: null, user: null }
      ],
      documents: [
        { name: 'FuelEU Statement.pdf', size: '1.8 MB', type: 'pdf' },
        { name: 'GHG Calculations.xlsx', size: '650 KB', type: 'excel' }
      ]
    },
    {
      id: 'rpt-003',
      title: 'IMO DCS Report 2024',
      type: 'IMO DCS',
      status: 'acknowledged',
      submissionDate: '2024-11-28',
      deadline: '2025-02-28',
      authority: 'IMO Secretariat',
      acknowledgment: 'IMO-DCS-2024-7891',
      progress: [
        { stage: 'Generated', completed: true, date: '2024-11-20', user: 'Michael Chen' },
        { stage: 'Reviewed', completed: true, date: '2024-11-25', user: 'Emma Thompson' },
        { stage: 'Approved', completed: true, date: '2024-11-27', user: 'Sarah Mitchell' },
        { stage: 'Submitted', completed: true, date: '2024-11-28', user: 'System' },
        { stage: 'Acknowledged', completed: true, date: '2024-12-05', user: 'IMO' }
      ],
      documents: [
        { name: 'IMO DCS Report.pdf', size: '3.1 MB', type: 'pdf' },
        { name: 'Fuel Data.xlsx', size: '1.4 MB', type: 'excel' },
        { name: 'Methodology.pdf', size: '980 KB', type: 'pdf' }
      ]
    },
    {
      id: 'rpt-004',
      title: 'CII Evidence Pack 2024',
      type: 'CII Evidence',
      status: 'draft',
      submissionDate: null,
      deadline: '2025-05-31',
      authority: 'Classification Society',
      acknowledgment: null,
      progress: [
        { stage: 'Generated', completed: true, date: '2024-12-20', user: 'David Rodriguez' },
        { stage: 'Reviewed', completed: false, date: null, user: null },
        { stage: 'Approved', completed: false, date: null, user: null },
        { stage: 'Submitted', completed: false, date: null, user: null },
        { stage: 'Acknowledged', completed: false, date: null, user: null }
      ],
      documents: [
        { name: 'CII Evidence Pack.pdf', size: '2.7 MB', type: 'pdf' },
        { name: 'Rating Calculations.xlsx', size: '780 KB', type: 'excel' }
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'acknowledged': return 'text-success bg-success/10';
      case 'submitted': return 'text-accent bg-accent/10';
      case 'in-review': return 'text-warning bg-warning/10';
      case 'draft': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'acknowledged': return 'CheckCircle2';
      case 'submitted': return 'Send';
      case 'in-review': return 'Eye';
      case 'draft': return 'Edit';
      default: return 'Circle';
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return 'FileText';
      case 'excel': return 'FileSpreadsheet';
      case 'xml': return 'Code';
      default: return 'File';
    }
  };

  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Report Status Tracker</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track submission history, approval workflows, and regulatory acknowledgments
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
          Refresh Status
        </Button>
      </div>
      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="maritime-card p-4 text-center">
          <div className="text-2xl font-bold text-success mb-1">1</div>
          <div className="text-sm text-muted-foreground">Acknowledged</div>
        </div>
        <div className="maritime-card p-4 text-center">
          <div className="text-2xl font-bold text-accent mb-1">1</div>
          <div className="text-sm text-muted-foreground">Submitted</div>
        </div>
        <div className="maritime-card p-4 text-center">
          <div className="text-2xl font-bold text-warning mb-1">1</div>
          <div className="text-sm text-muted-foreground">In Review</div>
        </div>
        <div className="maritime-card p-4 text-center">
          <div className="text-2xl font-bold text-muted-foreground mb-1">1</div>
          <div className="text-sm text-muted-foreground">Draft</div>
        </div>
      </div>
      {/* Report List */}
      <div className="space-y-4">
        {reportStatuses?.map((report) => {
          const daysUntilDeadline = getDaysUntilDeadline(report?.deadline);
          const isExpanded = selectedReport === report?.id;
          
          return (
            <div
              key={report?.id}
              className={`maritime-card transition-maritime ${
                isExpanded ? 'ring-2 ring-primary bg-primary/5' : ''
              }`}
            >
              <div
                className="p-6 cursor-pointer"
                onClick={() => setSelectedReport(isExpanded ? null : report?.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={getStatusIcon(report?.status)} size={20} strokeWidth={2} className="text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{report?.title}</h3>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(report?.status)}`}>
                          {report?.status?.replace('-', ' ')}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Authority: </span>
                          <span className="text-foreground">{report?.authority}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Deadline: </span>
                          <span className={`font-medium ${
                            daysUntilDeadline < 30 ? 'text-error' : 
                            daysUntilDeadline < 60 ? 'text-warning' : 'text-foreground'
                          }`}>
                            {new Date(report.deadline)?.toLocaleDateString()} ({daysUntilDeadline} days)
                          </span>
                        </div>
                        {report?.submissionDate && (
                          <div>
                            <span className="text-muted-foreground">Submitted: </span>
                            <span className="text-foreground">{new Date(report.submissionDate)?.toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      
                      {report?.acknowledgment && (
                        <div className="mt-2 text-sm">
                          <span className="text-muted-foreground">Acknowledgment: </span>
                          <span className="font-data text-foreground">{report?.acknowledgment}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Icon 
                    name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                    size={20} 
                    strokeWidth={2} 
                    className="text-muted-foreground"
                  />
                </div>
              </div>
              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-6 pb-6 border-t border-border">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Progress Timeline */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-4">Progress Timeline</h4>
                      <div className="space-y-4">
                        {report?.progress?.map((stage, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              stage?.completed 
                                ? 'bg-success text-success-foreground' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {stage?.completed ? (
                                <Icon name="Check" size={14} strokeWidth={2} />
                              ) : (
                                <span className="text-xs font-medium">{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${
                                stage?.completed ? 'text-foreground' : 'text-muted-foreground'
                              }`}>
                                {stage?.stage}
                              </div>
                              {stage?.completed && (
                                <div className="text-xs text-muted-foreground">
                                  {stage?.date} • {stage?.user}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Documents */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-4">Documents</h4>
                      <div className="space-y-3">
                        {report?.documents?.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <Icon name={getFileIcon(doc?.type)} size={16} strokeWidth={2} className="text-primary" />
                              <div>
                                <div className="text-sm font-medium text-foreground">{doc?.name}</div>
                                <div className="text-xs text-muted-foreground">{doc?.size}</div>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" iconName="Download">
                              Download
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="Clock" size={14} strokeWidth={2} />
                      <span>Last updated: {new Date()?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
                        View Report
                      </Button>
                      {report?.status === 'draft' && (
                        <Button size="sm" iconName="Send" iconPosition="left">
                          Submit Report
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportStatusTracker;